import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Phone, User, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../helpers/api.helper";

const money = (amount: number) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount / 100);
type Contact = { mobileNumber: string; countryCode: string; email: string };
type Owner = { mobileNumber?: string; bureauName?: string; bureauAddress?: string };
type Access = {
  allowed: boolean; eligible: boolean; state: string; reason: string;
  contactViewCost: number; balance: number; insufficientBalance: boolean; contact?: Contact;
};

export default function PaidContactDetails({ profileId, owner, hasPaidMembership, onUnlocked }: {
  profileId: string;
  owner?: Owner;
  hasPaidMembership: boolean;
  onUnlocked?: (contact: Contact) => void;
}) {
  const navigate = useNavigate();
  const [access, setAccess] = useState<Access | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<"options" | "owner" | "profile">("options");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const callback = useRef(onUnlocked);
  callback.current = onUnlocked;
  const active = useRef(true);
  const sequence = useRef(0);
  const busy = useRef(false);
  const base = `/profiles/${encodeURIComponent(profileId)}`;

  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    setError("");
    try {
      const result = await getRequest(`${base}/contact-access`);
      if (!active.current || request !== sequence.current) return;
      if (result.success && result.data) {
        setAccess(result.data);
        if (result.data.allowed && result.data.contact) callback.current?.(result.data.contact);
      } else {
        setAccess(null);
        setError(result.message || "Unable to load contact access.");
      }
    } catch {
      if (active.current && request === sequence.current) setError("Unable to load contact access.");
    } finally {
      if (active.current && request === sequence.current) setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    active.current = true;
    void load();
    return () => { active.current = false; sequence.current += 1; };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      dialog.animate?.([{ transform: "translateY(100%)" }, { transform: "translateY(0)" }], { duration: 250, easing: "ease-out" });
    }
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const unlock = async () => {
    if (busy.current || !access || !access.eligible || access.allowed || access.insufficientBalance) return;
    busy.current = true;
    setPaying(true);
    setError("");
    try {
      const result = await postRequest(`${base}/unlock-contact`, { expectedCost: access.contactViewCost });
      if (!active.current) return;
      if (result.success && result.data) {
        setAccess(result.data);
        if (result.data.allowed && result.data.contact) callback.current?.(result.data.contact);
        window.dispatchEvent(new Event("bureau-wallet-updated"));
      } else {
        await load();
        if (active.current) setError(result.message || "Unable to unlock contact details.");
      }
    } catch {
      if (active.current) setError("Unable to unlock contact details. Please refresh contact access before trying again.");
    } finally {
      busy.current = false;
      if (active.current) setPaying(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setSection("options"); setOpen(true); void load(); }}
        aria-haspopup="dialog"
        className="flex w-full items-center justify-center gap-2 rounded border border-gray-300 px-4 py-2 text-xs font-semibold hover:bg-gray-50 sm:text-sm"
      >
        <Phone size={16} /> View Contact Details
      </button>
      {open && (
        <dialog
          ref={dialogRef}
          aria-labelledby="contact-details-title"
          onCancel={(event) => { event.preventDefault(); if (!busy.current) setOpen(false); }}
          onClick={(event) => {
            if (event.target !== event.currentTarget || busy.current) return;
            const bounds = event.currentTarget.getBoundingClientRect();
            if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) setOpen(false);
          }}
          className="fixed inset-x-0 top-auto bottom-0 m-0 mx-auto max-h-[90dvh] w-full max-w-xl overflow-y-auto rounded-t-3xl border-0 bg-white p-0 text-sm shadow-xl backdrop:bg-black/50"
        >
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-gray-300" />
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div className="flex items-center gap-2">
              {section !== "options" && <button type="button" disabled={paying} onClick={() => setSection("options")} aria-label="Back to contact options" className="rounded-full p-2 hover:bg-gray-100 disabled:opacity-50"><ArrowLeft size={18} /></button>}
              <h2 id="contact-details-title" className="text-lg font-bold text-gray-900">{section === "owner" ? "Bureau Owner Contact Details" : section === "profile" ? "Profile Contact Details" : "View Contact Details"}</h2>
            </div>
            <button type="button" disabled={paying} onClick={() => setOpen(false)} aria-label="Close contact details" className="rounded-full p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"><X size={20} /></button>
          </div>
          <div className="space-y-4 px-5 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            {section === "options" && (
              <div className="grid gap-3">
                {owner && <button type="button" onClick={() => {
                  if (!hasPaidMembership) {
                    setOpen(false);
                    navigate("/admin/membership/plans", { state: { fromShare: true } });
                    return;
                  }
                  setSection("owner");
                }} className="flex items-center gap-3 rounded-xl border border-purple-100 p-4 text-left font-semibold text-purple-800 hover:bg-purple-50"><Phone size={20} /> Bureau Owner Contact Details</button>}
                <button type="button" onClick={() => setSection("profile")} className="flex items-center gap-3 rounded-xl border border-purple-100 p-4 text-left font-semibold text-purple-800 hover:bg-purple-50"><User size={20} /> Profile Contact Details</button>
              </div>
            )}
            {section === "owner" && owner && (
              <div className="space-y-3 text-gray-700">
                {owner.bureauName?.trim() && (
                  <p className="font-semibold text-black [overflow-wrap:anywhere]">{owner.bureauName}</p>
                )}
                {owner.bureauAddress?.trim() && (
                  <p className="whitespace-pre-line text-black [overflow-wrap:anywhere]">{owner.bureauAddress}</p>
                )}
                {owner.mobileNumber?.trim() ? <>
                  <p className="break-all">{owner.mobileNumber}</p>
                  <div className="flex flex-wrap gap-3">
                    <a href={`tel:${owner.mobileNumber}`} className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white"><Phone size={16} /> Call Owner</a>
                    <a href={`https://wa.me/${owner.mobileNumber.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white">WhatsApp</a>
                  </div>
                </> : <p>Owner contact details are unavailable.</p>}
              </div>
            )}
            {section === "profile" && (
              <>
                {loading && <p className="text-gray-600">Loading contact access…</p>}
                {error && <p role="alert" className="text-red-700">{error}</p>}
                {!loading && !access && <button type="button" onClick={() => void load()} className="font-semibold text-purple-700">Retry</button>}
                {!loading && access?.allowed && access.contact ? (
                  <div className="space-y-3">
                    <p className="font-semibold text-emerald-700">{access.state === "unlocked" ? "Contact Unlocked — no additional charge" : "Contact Details"}</p>
                    {access.contact.mobileNumber ? <>
                      <p className="break-all text-gray-800">{access.contact.countryCode} {access.contact.mobileNumber}</p>
                      <div className="flex flex-wrap gap-3">
                        <a className="font-semibold text-purple-700 underline" href={`tel:${access.contact.countryCode}${access.contact.mobileNumber}`}>Call</a>
                        <a className="font-semibold text-emerald-700 underline" href={`https://wa.me/${`${access.contact.countryCode}${access.contact.mobileNumber}`.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                      </div>
                    </> : <p className="text-gray-600">No phone number is available.</p>}
                    {access.contact.email && <a className="block break-all text-purple-700 underline" href={`mailto:${access.contact.email}`}>{access.contact.email}</a>}
                  </div>
                ) : !loading && access && (
                  access.eligible ? <>
                    <p className="text-gray-700">{money(access.contactViewCost)} will be deducted from your Bureau Wallet to view this profile's contact details. The profile owner's Bureau receives this amount as separate earnings.</p>
                    <p className="font-semibold text-gray-900">Your Wallet Balance: {money(access.balance)}</p>
                    <p className="text-xs text-gray-500">You pay only once per profile. Viewing the Bureau owner's contact details is free.</p>
                    {access.insufficientBalance && <p className="text-red-700">Insufficient wallet balance. Please contact your Bureau Administrator to add funds.</p>}
                    <div className="flex justify-end gap-3">
                      <button type="button" disabled={paying} onClick={() => setSection("options")} className="rounded-lg border px-4 py-2 disabled:opacity-50">Back</button>
                      <button type="button" disabled={paying || access.insufficientBalance} onClick={() => void unlock()} className="rounded-lg bg-purple-600 px-4 py-2 font-semibold text-white disabled:opacity-50">{paying ? "Processing…" : `Ok Proceed — ${money(access.contactViewCost)}`}</button>
                    </div>
                  </> : <p className="text-base font-medium leading-relaxed text-gray-800">{access.reason || "Contact access is unavailable for this profile."}</p>
                )}
              </>
            )}
          </div>
        </dialog>
      )}
    </>
  );
}
